import { defineStore } from 'pinia';
import { Capacitor } from '@capacitor/core';
import { userManager, getBearerToken, nativeSignIn, nativeSignOut } from '@/services/oidc';

// Auth via OIDC (auth-code + PKCE) against the redwolffoundry IdP (auth-staging).
// The IdP owns credentials now — there is no local password/register/device flow.
// We mirror identity (sub/email/roles) from the OIDC id_token claims into store
// state so the existing role-gated UI keeps working without a server round-trip.
//
// `roles` is read from the id_token claims if the IdP includes them; otherwise it
// defaults to [] (role-gated UI degrades gracefully — this slice only needs
// app-boot + login + the restaurants list to work).
export const useAuthStore = defineStore('auth', {
	state: () => ({
		userId: localStorage.getItem('userId') || null,
		userEmail: localStorage.getItem('userEmail') || null,
		userRoles: JSON.parse(localStorage.getItem('userRoles') || '[]'),
		isLoggedIn: !!localStorage.getItem('userId'),
		// Whether the DB user (real id + roles) has been resolved this session. A normal
		// member legitimately has 0 roles, so gating loadDbUser on `roles.length === 0`
		// re-fetched on every guarded navigation; this one-shot flag fixes that. (L-5)
		dbUserResolved: false
	}),

	getters: {
		isAuthenticated: (state) => !!state.userId,
		getUserId: (state) => state.userId,
		getUserEmail: (state) => state.userEmail,
		getUserRoles: (state) => state.userRoles,
		hasCircleCreatorRole: (state) => state.userRoles.includes('circleCreator'),
		hasAdminRole: (state) => state.userRoles.includes('admin')
	},

	actions: {
		// Persist identity pulled from the OIDC id_token claims/profile.
		setAuth({ userId, userEmail, userRoles = [] }) {
			this.userId = userId;
			this.userEmail = userEmail;
			this.userRoles = userRoles;
			this.isLoggedIn = true;

			localStorage.setItem('userId', userId);
			if (userEmail) localStorage.setItem('userEmail', userEmail);
			localStorage.setItem('userRoles', JSON.stringify(userRoles));
		},

		clearAuth() {
			this.userId = null;
			this.userEmail = null;
			this.userRoles = [];
			this.isLoggedIn = false;
			this.dbUserResolved = false;

			localStorage.removeItem('userId');
			localStorage.removeItem('userEmail');
			localStorage.removeItem('userRoles');
		},

		// Begin sign-in: redirect to the IdP for auth-code + PKCE.
		// Native (Capacitor) can't do a same-window redirect, so it runs the flow in an external
		// browser and completes here (hydrating identity just like /callback does on web), then
		// returns the post-login path for the caller to navigate to. Web navigates away.
		async login(redirect = '/') {
			if (Capacitor.isNativePlatform()) {
				const user = await nativeSignIn({ redirect });
				this.hydrateFromOidcUser(user);
				await this.loadDbUser();
				return redirect;
			}
			return userManager.signinRedirect({ state: { redirect } });
		},

		// Complete sign-in on return to /callback. Hydrates identity from the
		// id_token profile claims and returns the post-login redirect path.
		async completeLogin() {
			const user = await userManager.signinRedirectCallback();
			this.hydrateFromOidcUser(user);
			await this.loadDbUser();
			return user?.state?.redirect || '/';
		},

		// The id_token's `sub` is the IdP id, not our DB user id, and carries no roles. Resolve
		// the real `user` record (DB id + roles) so membership filters and role-gated UI work.
		async loadDbUser() {
			try {
				const token = await getBearerToken();
				if (!token || !this.userEmail) return;
				// Filter by email server-side (authz already scopes a member to their own
				// record, but this keeps the payload to the one row we want).
				const where = encodeURIComponent(JSON.stringify({ email: { eq: this.userEmail } }));
				const res = await fetch(`${process.env.VUE_APP_API_URL}/user?where=${where}`, {
					headers: { Authorization: `Bearer ${token}` }
				});
				if (!res.ok) return;
				const list = await res.json();
				const me = (Array.isArray(list) ? list : []).find((u) => u.email === this.userEmail);
				if (me) {
					this.userId = me.id;
					this.userRoles = Array.isArray(me.roles) ? me.roles : [];
					this.dbUserResolved = true;
					localStorage.setItem('userId', me.id);
					localStorage.setItem('userRoles', JSON.stringify(this.userRoles));
				}
			} catch { /* keep id_token-derived identity on failure */ }
		},

		hydrateFromOidcUser(user) {
			if (!user) return;
			const profile = user.profile || {};
			// Only mirror email + logged-in state from the id_token; the DB user id + roles are
			// resolved separately via loadDbUser() (the sub is not our DB id and has no roles).
			this.userEmail = profile.email || this.userEmail || null;
			this.isLoggedIn = true;
			if (this.userEmail) localStorage.setItem('userEmail', this.userEmail);
		},

		// Restore a cached OIDC session on app start / before a guarded navigation.
		// Returns true when a non-expired session exists.
		async checkAuth() {
			const user = await userManager.getUser();
			if (user && !user.expired) {
				this.hydrateFromOidcUser(user);
				if (!this.dbUserResolved) await this.loadDbUser();
				return true;
			}
			this.clearAuth();
			return false;
		},

		// Roles live in the id_token now; re-read the cached OIDC user to refresh
		// the mirrored state. Returns true on success.
		async refreshUserFromServer() {
			return this.checkAuth();
		},

		async logout() {
			this.clearAuth();

			// Clear other stores' cached data so a different identity doesn't see it.
			const { useCirclesStore } = require('./circles');
			const { useRestaurantsStore } = require('./restaurants');
			const { useVisitsStore } = require('./visits');
			const { useReviewsStore } = require('./reviews');
			const { useInvitationsStore } = require('./invitations');

			useCirclesStore().$reset();
			useRestaurantsStore().$reset();
			useVisitsStore().$reset();
			useReviewsStore().$reset();
			useInvitationsStore().$reset();

			// Native: there's no same-window redirect to the IdP end_session endpoint. Remove the
			// cached User (so a stale id_token can't silently re-auth on the next checkAuth) and
			// reload into /login.
			if (Capacitor.isNativePlatform()) {
				await nativeSignOut();
				window.location.assign('/login');
				return;
			}

			// RP-initiated logout: redirect to the IdP end_session endpoint so the SSO
			// session cookie is cleared too (signoutRedirect also purges the cached
			// id_token/refresh token before it navigates). If that round-trip fails for
			// any reason, the cached OIDC user must STILL be removed locally — otherwise
			// checkAuth() re-hydrates from the stale id_token and silently signs us back in.
			try {
				await userManager.signoutRedirect();
			} catch {
				await userManager.removeUser();
				window.location.assign('/login');
			}
		}
	}
});
